import { KLineData } from "klinecharts";
import { readStyle } from "./indicatorUtils";

function smmaStep(prev: number, value: number, period: number): number {
  return (prev * (period - 1) + value) / period;
}

export const createGatorOscillator = (settings: any) => {
  const values = settings?.values ?? {};
  const styles = settings?.styles ?? settings?.styleSettings ?? {};
  const jawPeriod = (values["Jaw Period"] as number) ?? 13;
  const jawShift = (values["Jaw Shift"] as number) ?? 8;
  const teethPeriod = (values["Teeth Period"] as number) ?? 8;
  const teethShift = (values["Teeth Shift"] as number) ?? 5;
  const lipsPeriod = (values["Lips Period"] as number) ?? 5;
  const lipsShift = (values["Lips Shift"] as number) ?? 3;

  const upperUp = readStyle(styles, "upperUp", "#26A69A");
  const upperDown = readStyle(styles, "upperDown", "#EF5350");
  const lowerUp = readStyle(styles, "lowerUp", "#26A69A");
  const lowerDown = readStyle(styles, "lowerDown", "#EF5350");

  return {
    name: "gator",
    shortName: "Gator",
    calcParams: [
      jawPeriod,
      jawShift,
      teethPeriod,
      teethShift,
      lipsPeriod,
      lipsShift,
    ],
    series: "normal" as const,
    figures: [
      { key: "upper", title: "Upper: ", type: "bar" },
      { key: "lower", title: "Lower: ", type: "bar" },
    ],
    styles: {
      bars: [
        {
          upColor: upperUp.color,
          downColor: upperDown.color,
          noChangeColor: "#888888",
        },
        {
          upColor: lowerUp.color,
          downColor: lowerDown.color,
          noChangeColor: "#888888",
        },
      ],
    },
    regenerateFigures: () => [
      { key: "upper", title: "Upper: ", type: "bar" },
      { key: "lower", title: "Lower: ", type: "bar" },
    ],
    calc: (dataList: KLineData[], indicator: { calcParams?: unknown[] }) => {
      const [jP, jS, tP, tS, lP, lS] = (indicator.calcParams ?? [
        jawPeriod,
        jawShift,
        teethPeriod,
        teethShift,
        lipsPeriod,
        lipsShift,
      ]) as [number, number, number, number, number, number];
      const n = dataList.length;
      const results: Array<{ upper?: number; lower?: number }> = new Array(n);
      for (let i = 0; i < n; i++) results[i] = {};

      const jaw: number[] = new Array(n);
      const teeth: number[] = new Array(n);
      const lips: number[] = new Array(n);

      let jawSmma = 0;
      let teethSmma = 0;
      let lipsSmma = 0;
      let jawSeed = 0;
      let teethSeed = 0;
      let lipsSeed = 0;

      for (let i = 0; i < n; i++) {
        const median = (dataList[i]!.high + dataList[i]!.low) / 2;

        if (i < jP) {
          jawSeed += median;
          if (i === jP - 1) jawSmma = jawSeed / jP;
        } else jawSmma = smmaStep(jawSmma, median, jP);
        if (i >= jP - 1) jaw[i] = jawSmma;

        if (i < tP) {
          teethSeed += median;
          if (i === tP - 1) teethSmma = teethSeed / tP;
        } else teethSmma = smmaStep(teethSmma, median, tP);
        if (i >= tP - 1) teeth[i] = teethSmma;

        if (i < lP) {
          lipsSeed += median;
          if (i === lP - 1) lipsSmma = lipsSeed / lP;
        } else lipsSmma = smmaStep(lipsSmma, median, lP);
        if (i >= lP - 1) lips[i] = lipsSmma;
      }

      for (let i = 0; i < n; i++) {
        const ji = i - jS;
        const ti = i - tS;
        const li = i - lS;
        if (
          ji >= jP - 1 &&
          ti >= tP - 1 &&
          li >= lP - 1 &&
          jaw[ji] !== undefined &&
          teeth[ti] !== undefined &&
          lips[li] !== undefined
        ) {
          results[i] = {
            upper: Math.abs(jaw[ji]! - teeth[ti]!),
            lower: -Math.abs(teeth[ti]! - lips[li]!),
          };
        }
      }

      return results;
    },
  };
};
